import {
  Component, EventEmitter, Input, OnDestroy, Output, effect, inject, signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SecurityInfoComponent } from './security-info.component';
import { TrackingApiService } from '../services/tracking-api.service';
import { TrackingStoreService } from '../services/tracking-store.service';
import { durationSecToStr, liveDuration, parseApiDate, shortId, statusClass, statusDot, statusLabel } from '../tracking-format.util';
import type { TrackingParticipant, TrackingSession } from '../tracking-demo.models';

type PanelTab = 'details' | 'participants';

/**
 * Slide-over panel for one session: details + participants roster with the admin
 * call controls (mute / remove / disconnect / blacklist).
 *
 * The old covert "Live View" (a reconstructed semantic mirror of the demo screen)
 * has been RETIRED: an admin now JOINS the real room as a hidden, receive-only
 * observer from the live table's "Join room" action and sees the actual
 * audio/video/slides. This panel keeps only the durable details + moderation
 * surface (still useful alongside, and for ended sessions in History).
 */
@Component({
  selector: 'app-session-details-panel',
  standalone: true,
  imports: [CommonModule, SecurityInfoComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './session-details-panel.component.html',
})
export class SessionDetailsPanelComponent implements OnDestroy {
  private readonly api = inject(TrackingApiService);
  private readonly store = inject(TrackingStoreService);

  @Input() set open(v: { sessionId: string; tab: PanelTab } | null) {
    this._open.set(v);
  }
  @Output() closed = new EventEmitter<void>();
  @Output() disconnectRequested = new EventEmitter<TrackingSession>();
  @Output() blacklistRequested = new EventEmitter<TrackingSession>();

  readonly _open = signal<{ sessionId: string; tab: PanelTab } | null>(null);
  readonly tab = signal<PanelTab>('details');
  readonly panelTabs: PanelTab[] = ['details', 'participants'];
  readonly session = signal<TrackingSession | null>(null);
  readonly participants = signal<TrackingParticipant[]>([]);
  readonly loading = signal(false);
  readonly now = this.store.now;

  readonly durationSecToStr = durationSecToStr;
  readonly liveDuration = liveDuration;
  readonly shortId = shortId;
  readonly statusClass = statusClass;
  readonly statusDot = statusDot;
  readonly statusLabel = statusLabel;

  private currentSid: string | null = null;
  private lastRosterGuestCount = -1;

  constructor() {
    // React to opening / switching sessions.
    effect(() => {
      const o = this._open();
      const sid = o?.sessionId ?? null;
      if (sid !== this.currentSid) {
        this.currentSid = sid;
        if (sid) this.load(sid);
      }
      if (o) this.tab.set(o.tab);
    });

    // Keep the joined-users roster fresh: refetch when the live session's guest count changes.
    effect(() => {
      const sessions = this.store.sessions();
      const sid = this.currentSid;
      if (!sid) return;
      const gc = sessions.find((s) => s.sessionId === sid)?.guestCount ?? 0;
      if (gc !== this.lastRosterGuestCount) {
        this.lastRosterGuestCount = gc;
        this.refreshParticipants(sid);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.actionErrorTimer) clearTimeout(this.actionErrorTimer);
    if (this.pendingRemoveTimer) clearTimeout(this.pendingRemoveTimer);
  }

  setTab(t: PanelTab): void {
    this.tab.set(t);
  }

  close(): void {
    this.currentSid = null;
    this._open.set(null);
    this.closed.emit();
  }

  private async load(sid: string): Promise<void> {
    this.loading.set(true);
    this.lastRosterGuestCount = -1;
    try {
      const detail = await this.api.getSession(sid);
      this.session.set(detail);
      this.participants.set(detail.participants || []);
      if (!detail.participants) {
        const p = await this.api.getParticipants(sid);
        this.participants.set(p.participants || []);
      }
    } catch {
      /* 401 handled by api; panel shows whatever loaded */
    } finally {
      this.loading.set(false);
    }
  }

  private async refreshParticipants(sid: string): Promise<void> {
    try {
      const p = await this.api.getParticipants(sid);
      if (this.currentSid === sid) this.participants.set(p.participants || []);
    } catch { /* best-effort — the roster keeps its last-known value */ }
  }

  // ── admin call controls (mute / remove / terminate) ──
  /** participantId with an in-flight action — disables that row's buttons. */
  readonly actionBusy = signal<string | null>(null);
  /** Last action failure, shown inline under the roster (auto-clears). */
  readonly actionError = signal<string | null>(null);
  /** Guest pending the two-step Remove confirmation. */
  readonly pendingRemove = signal<string | null>(null);
  private actionErrorTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingRemoveTimer: ReturnType<typeof setTimeout> | null = null;

  /** The session is in a state where call-control commands can still land. */
  callActive(): boolean {
    const st = this.session()?.status;
    return st === 'live' || st === 'on_hold' || st === 'connecting' || st === 'waiting';
  }

  /** True when this participant is currently in the call (a command can reach them). */
  isJoined(p: TrackingParticipant): boolean {
    if (p.role === 'guest') return p.status === 'accepted' && !p.leftAt;
    return p.status === 'joined';
  }

  /** Mute/unmute is offered for the host + joined guests (the AI has no controllable mic). */
  canMute(p: TrackingParticipant): boolean {
    return this.callActive() && p.role !== 'ai' && this.isJoined(p);
  }

  /** Remove is offered for guests only (terminate the session to end the host's call). */
  canRemove(p: TrackingParticipant): boolean {
    return this.callActive() && p.role === 'guest'
      && !['blocked', 'rejected', 'expired', 'left'].includes(p.status);
  }

  micOff(p: TrackingParticipant): boolean { return p.micStatus === 'off'; }

  micChip(p: TrackingParticipant): { text: string; cls: string } {
    const mic = p.micStatus || 'unknown';
    if (mic === 'on') return { text: 'Mic on', cls: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400' };
    if (mic === 'off') return { text: 'Muted', cls: 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400' };
    return { text: 'Mic —', cls: 'bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400' };
  }

  camChip(p: TrackingParticipant): { text: string; cls: string } | null {
    if (p.role === 'ai' || p.camStatus == null || p.camStatus === 'unknown') return null;
    return p.camStatus === 'on'
      ? { text: 'Cam on', cls: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400' }
      : { text: 'Cam off', cls: 'bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400' };
  }

  async toggleMute(p: TrackingParticipant): Promise<void> {
    if (this.actionBusy() || !this.currentSid) return;
    const mute = p.micStatus !== 'off'; // 'on' or 'unknown' → mute; 'off' → unmute
    this.actionBusy.set(p.participantId);
    try {
      await this.api.muteParticipant(this.currentSid, p.participantId, mute);
      // Optimistic; the demo's own state re-broadcast converges it moments later.
      this.patchParticipantState(p.participantId, mute ? 'off' : 'on');
    } catch (err: any) {
      this.showActionError(err?.error?.error || `Could not ${mute ? 'mute' : 'unmute'} ${p.name}.`);
    } finally {
      this.actionBusy.set(null);
    }
  }

  /** Two-step Remove: first click arms the confirmation, second click executes. */
  async removeGuest(p: TrackingParticipant): Promise<void> {
    if (this.actionBusy() || !this.currentSid) return;
    if (this.pendingRemove() !== p.participantId) {
      this.pendingRemove.set(p.participantId);
      if (this.pendingRemoveTimer) clearTimeout(this.pendingRemoveTimer);
      this.pendingRemoveTimer = setTimeout(() => this.pendingRemove.set(null), 5000);
      return;
    }
    this.pendingRemove.set(null);
    this.actionBusy.set(p.participantId);
    try {
      await this.api.removeParticipant(this.currentSid, p.participantId, 'admin_removed');
      this.refreshParticipants(this.currentSid);
    } catch (err: any) {
      this.showActionError(err?.error?.error || `Could not remove ${p.name}.`);
    } finally {
      this.actionBusy.set(null);
    }
  }

  private showActionError(message: string): void {
    this.actionError.set(message);
    if (this.actionErrorTimer) clearTimeout(this.actionErrorTimer);
    this.actionErrorTimer = setTimeout(() => this.actionError.set(null), 6000);
  }

  private patchParticipantState(pid: string, micStatus?: string | null, camStatus?: string | null): void {
    const list = this.participants();
    const idx = list.findIndex((x) => x.participantId === pid);
    if (idx < 0) {
      // A participant we don't have yet declared state (joined between roster refreshes).
      if (this.currentSid) this.refreshParticipants(this.currentSid);
      return;
    }
    const next = [...list];
    next[idx] = {
      ...next[idx],
      ...(micStatus != null ? { micStatus } : {}),
      ...(camStatus != null ? { camStatus } : {}),
    };
    this.participants.set(next);
  }

  /** Count of participants currently joined (host + guests + AI), for the roster header. */
  joinedCount(): number {
    return this.participants().length;
  }

  copyMeetingUrl(): void {
    const url = this.session()?.meetingUrl;
    if (url && navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
  }

  roleBadge(role: string): string {
    return role === 'host' ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-300'
      : role === 'ai' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'
      : 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300';
  }

  /** Dot color for a participant's presence in the roster cards. */
  presenceDot(p: TrackingParticipant): string {
    if (this.isJoined(p)) return 'bg-emerald-500';
    if (p.status === 'pending') return 'bg-amber-400';
    if (p.status === 'blocked' || p.status === 'rejected') return 'bg-red-500';
    return 'bg-gray-400';
  }

  /** Short HH:MM for roster timestamps ('—' when unknown). */
  timeShort(iso: string | null | undefined): string {
    const d = parseApiDate(iso);
    return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
  }

  /** Guests currently in the call (for the control bar counter). */
  guestsJoined(): number {
    return this.participants().filter((p) => p.role === 'guest' && this.isJoined(p)).length;
  }
}
